import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api, euro, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, Pencil } from "@phosphor-icons/react";
import MoneyInput from "../components/MoneyInput";

const TYPES = [
    { value: "cassa", label: "Cassa" },
    { value: "cassaforte", label: "Cassaforte" },
    { value: "banca", label: "Banca" },
    { value: "generico", label: "Generico" },
    { value: "utili_accantonati", label: "Utili Accantonati" },
];

export default function Wallets() {
    const { agencyId, user } = useAuth();
    const [wallets, setWallets] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null); // wallet object being edited
    const [confirmDel, setConfirmDel] = useState(null);
    const [form, setForm] = useState({
        name: "",
        type: "cassa",
        initial_balance: "",
    });
    const [editForm, setEditForm] = useState({
        name: "",
        initial_balance: "",
        balance: "",
    });
    const canEdit = ["owner", "agency_manager"].includes(user?.role);

    const load = () => {
        if (!agencyId) return;
        api.get(`/wallets`, { params: { agency_id: agencyId } }).then((r) =>
            setWallets(r.data),
        );
    };
    useEffect(() => {
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agencyId]);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/wallets", {
                name: form.name,
                type: form.type,
                initial_balance: parseFloat(form.initial_balance) || 0,
                agency_id: agencyId,
            });
            toast.success("Wallet creato");
            setOpen(false);
            setForm({ name: "", type: "cassa", initial_balance: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const startEdit = (w) => {
        setEditing(w);
        setEditForm({
            name: w.name,
            initial_balance: String(w.initial_balance ?? 0),
            balance: String(w.balance ?? 0),
        });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        try {
            await api.patch(`/wallets/${editing.id}`, {
                name: editForm.name,
                initial_balance: parseFloat(editForm.initial_balance),
                balance: parseFloat(editForm.balance),
            });
            toast.success("Wallet aggiornato");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async () => {
        if (!confirmDel) return;
        try {
            await api.delete(`/wallets/${confirmDel.id}`);
            toast.success("Wallet eliminato");
            setConfirmDel(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="wallets-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <div className="overline mb-2">Patrimonio</div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
                        Wallet
                    </h1>
                </div>
                {canEdit && (
                    <button
                        onClick={() => setOpen(true)}
                        className="btn-primary flex items-center gap-2"
                        data-testid="add-wallet-btn"
                    >
                        <Plus size={14} weight="bold" /> Nuovo wallet
                    </button>
                )}
            </div>

            <div className="mb-6 p-4 bg-white border border-zinc-200 text-sm text-zinc-700">
                Il <strong>saldo iniziale</strong> dei wallet di tipo{" "}
                <strong>cassa, cassaforte e banca</strong> compone il{" "}
                <strong>&quot;Fondo Cassa&quot;</strong> della formula di quadratura. Modificalo
                con il bottone matita per correggere errori di apertura senza dover
                ricreare il wallet.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-room bg-white">
                {wallets.map((w) => (
                    <div
                        key={w.id}
                        className="p-6 grid-cell relative"
                        data-testid={`wallet-card-${w.id}`}
                    >
                        <div className="overline mb-3">{w.type.replace("_", " ")}</div>
                        <div className="text-lg font-bold mb-2">{w.name}</div>
                        <div
                            className="metric-huge tabular-nums"
                            style={{ color: w.balance < 0 ? "#FF2A00" : "#09090b" }}
                        >
                            {euro(w.balance)}
                        </div>
                        <div className="text-xs text-zinc-500 mt-2 tabular-nums">
                            saldo iniziale {euro(w.initial_balance ?? 0)}
                        </div>
                        {canEdit && (
                            <div className="absolute top-3 right-3 flex gap-1">
                                <button
                                    onClick={() => startEdit(w)}
                                    className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-ink hover:bg-zinc-100 transition-colors"
                                    title="Modifica wallet"
                                    data-testid={`edit-wallet-${w.id}`}
                                    aria-label="Modifica wallet"
                                >
                                    <Pencil size={14} weight="bold" />
                                </button>
                                <button
                                    onClick={() => setConfirmDel(w)}
                                    className="inline-flex items-center justify-center w-9 h-9 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 transition-colors"
                                    title="Elimina wallet"
                                    data-testid={`delete-wallet-${w.id}`}
                                    aria-label="Elimina wallet"
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                {wallets.length === 0 && (
                    <div className="p-12 col-span-full text-center text-zinc-500">
                        Nessun wallet configurato.
                    </div>
                )}
            </div>

            {open && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setOpen(false)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={create}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="wallet-form"
                    >
                        <div className="overline mb-3">Nuovo Wallet</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            Crea wallet.
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="wallet-form-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Tipo</div>
                            <select
                                className="inp"
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                data-testid="wallet-form-type"
                            >
                                {TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Saldo iniziale €</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={form.initial_balance}
                                onChange={(v) =>
                                    setForm({ ...form, initial_balance: v })
                                }
                                placeholder="0,00"
                                data-testid="wallet-form-balance"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Cassa/Cassaforte/Banca: contribuisce al &quot;Fondo Cassa&quot;.
                            </div>
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="wallet-form-submit"
                            >
                                Crea
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {editing && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-start justify-end"
                    onClick={() => setEditing(null)}
                >
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={saveEdit}
                        className="w-full max-w-md h-full bg-white border-l border-zinc-200 p-8 overflow-y-auto"
                        data-testid="wallet-edit-form"
                    >
                        <div className="overline mb-3">Modifica Wallet</div>
                        <h2 className="text-3xl font-black tracking-tighter mb-8">
                            {editing.name}
                        </h2>
                        <label className="block mb-4">
                            <div className="overline mb-2">Nome</div>
                            <input
                                className="inp"
                                value={editForm.name}
                                onChange={(e) =>
                                    setEditForm({ ...editForm, name: e.target.value })
                                }
                                required
                                data-testid="wallet-edit-name"
                            />
                        </label>
                        <label className="block mb-4">
                            <div className="overline mb-2">Saldo iniziale €</div>
                            <MoneyInput
                                className="inp tabular-nums"
                                value={editForm.initial_balance}
                                onChange={(v) =>
                                    setEditForm({
                                        ...editForm,
                                        initial_balance: v,
                                    })
                                }
                                placeholder="0,00"
                                data-testid="wallet-edit-initial"
                            />
                            <div className="text-xs text-zinc-500 mt-1">
                                Modifica del saldo iniziale: regola anche il saldo corrente
                                della stessa differenza per mantenere coerenti le transazioni.
                            </div>
                        </label>
                        <label className="block mb-8">
                            <div className="overline mb-2">Saldo corrente € (override diretto)</div>
                            <MoneyInput
                                className="inp-huge tabular-nums"
                                value={editForm.balance}
                                onChange={(v) =>
                                    setEditForm({ ...editForm, balance: v })
                                }
                                placeholder="0,00"
                                data-testid="wallet-edit-balance"
                            />
                            <div className="text-xs text-amber-700 mt-1">
                                Override diretto del saldo: usalo solo per correzioni straordinarie.
                            </div>
                        </label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setEditing(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                type="submit"
                                className="btn-primary flex-1"
                                data-testid="wallet-edit-submit"
                            >
                                Salva
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {confirmDel && (
                <div
                    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => setConfirmDel(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white border border-zinc-200 p-8"
                        data-testid="confirm-del-wallet"
                    >
                        <div className="overline mb-3">Eliminare wallet?</div>
                        <h2 className="text-2xl font-black tracking-tighter mb-4">
                            {confirmDel.name}
                        </h2>
                        <p className="text-sm text-zinc-700 mb-6">
                            Saldo corrente {euro(confirmDel.balance)}. Le transazioni che
                            riferiscono a questo wallet rimarranno nel database ma il wallet non
                            sarà più disponibile.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setConfirmDel(null)}
                                className="btn-secondary flex-1"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={remove}
                                className="btn-destructive flex-1"
                                data-testid="confirm-del-wallet-btn"
                            >
                                Elimina
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

